import dynamic from 'next/dynamic';
import styles from './bio.module.scss';
import { useEffect, useRef, useState } from 'react';
import { useScrollTo } from 'hooks/use-scroll-to';
import { useTranslation } from 'react-i18next';
import { getScrollTargetWithHeader } from '@players/helpers/get-scroll-target-with-header';

const Button = dynamic(() => import('core/generic/elements/button'));
const RichText = dynamic(() => import('core/generic/components/general/rich-text'));

type BioProps = {
    content: string;
};

export const Bio = ({ content }: BioProps) => {
    const { scrollToTop } = useScrollTo();
    const contentBodyRef = useRef<HTMLDivElement>(null);
    const [condensed, setCondensed] = useState(false);
    const [isTooLong, setIsTooLong] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        setIsTooLong(false);
        setCondensed(false);
        const measure = () => {
            if (contentBodyRef.current) {
                const h = contentBodyRef.current.clientHeight;
                const w = window.innerWidth;
                const tooLong = (w < 768 && h > 512) || (w < 1440 && h > 384) || h > 396;
                setIsTooLong(tooLong);
                setCondensed(tooLong);
            }
        };

        const timeout = setTimeout(measure, 300);

        return () => {
            clearTimeout(timeout);
        };
    }, [content]);

    const handleMoreLessClick = () => {
        if (!condensed) {
            requestAnimationFrame(() => {
                const scrollTarget = getScrollTargetWithHeader(contentBodyRef);
                if (scrollTarget !== null) {
                    scrollToTop({
                        offset: scrollTarget,
                    });
                }
            });
        }
        setCondensed(!condensed);
    };



    return (
        <div
            className={`${styles['bio']} ${condensed ? styles['bio--condensed'] : ''}`}
            ref={contentBodyRef}
        >
            <RichText content={content} placeholder={' '} />

            {isTooLong && (
                <div className={styles['bio__read-more']}>
                    <Button
                        dimension="small"
                        label={
                            condensed
                                ? t('players_profile.read_more', 'Read more')
                                : t('players_profile.read_less', 'Read less')
                        }
                        tabIndex={0}
                        variant="tertiary"
                        icon={condensed ? 'ChevronDown' : 'ChevronUp'}
                        iconPosition="right"
                        onClick={() => {
                            handleMoreLessClick();
                        }}
                    />
                </div>
            )}
        </div>
    );
};
